import { Check, Loader2, Mail, MoreHorizontal, Paperclip, RefreshCw } from 'lucide-react';
import React, { useCallback, useState } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useSyncEmailsMutation } from '@/services/api/clientCommunicationsService';
import { UnifiedCommunication } from '@/services/types/client-communications-types';

import { useClientCommunications } from '../ClientCommunicationsContext';

// Format date for display - matches mock format "Nov 5, 2025"
const formatDate = (timestamp: string) => {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

interface EmailRowProps {
  email: UnifiedCommunication;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  isLast: boolean;
}

const EmailRow: React.FC<EmailRowProps> = ({ email, isSelected, onToggleSelect, isLast }) => {
  const { selectCommunication, isRead } = useClientCommunications();

  const isInbound = email.direction === 'inbound';
  const read = isRead(email.id);
  const isUnread = !read && isInbound;

  const handleSelect = () => {
    selectCommunication(email);
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleSelect(email.id);
  };

  // Clean and truncate preview text
  const getPreview = () => {
    const text = email.preview || email.content || '';
    const cleanText = text.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    // Truncate to ~75 chars like the mock
    return cleanText.length > 75 ? `${cleanText.slice(0, 75)}...` : cleanText;
  };

  // Get attachments
  const attachments = email.attachments ?? [];
  const hasAttachments = email.has_attachments || attachments.length > 0;

  return (
    <div
      className={cn(
        'flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors',
        // Read = gray background, Unread = white background
        isUnread
          ? 'bg-white hover:bg-zinc-50'
          : 'bg-zinc-100/80 hover:bg-zinc-200/70',
        !isLast && 'border-b border-zinc-200/60',
      )}
      onClick={handleSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect();
        }
      }}
      role="button"
      tabIndex={0}
    >
      {/* Custom Checkbox */}
      <div
        className={cn(
          'relative shrink-0 size-4 rounded-[4px] border-2 cursor-pointer mt-0.5',
          isSelected ? 'bg-primary border-primary' : 'border-zinc-400',
        )}
        onClick={handleCheckboxClick}
        onKeyDown={(e) => e.stopPropagation()}
        role="checkbox"
        aria-checked={isSelected}
        tabIndex={-1}
      >
        {isSelected && (
          <Check
            className="absolute inset-0 w-3 h-3 text-white m-auto pointer-events-none"
            strokeWidth={3}
          />
        )}
      </div>

      {/* Subject column */}
      <div className="shrink-0 flex flex-col gap-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              'text-sm',
              isUnread ? 'font-bold text-zinc-900' : 'font-semibold text-zinc-800',
            )}
          >
            {email.subject || '(No subject)'}
          </span>
          {email.thread_count && email.thread_count > 1 && (
            <span className="shrink-0 text-xs font-semibold text-zinc-600">
              ({email.thread_count})
            </span>
          )}
        </div>

        {/* Attachments row */}
        {hasAttachments && (
          <div className="flex gap-1.5 flex-wrap">
            {attachments.slice(0, 3).map((attachment, idx) => (
              <div
                key={attachment.id || idx}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-200 rounded text-xs font-medium text-zinc-700"
              >
                <Paperclip className="w-3 h-3 text-zinc-600" />
                <span className="max-w-[120px] truncate">{attachment.filename}</span>
              </div>
            ))}
            {attachments.length > 3 && (
              <div className="inline-flex items-center px-2 py-0.5 bg-zinc-200 rounded text-xs font-medium text-zinc-700">
                +{attachments.length - 3} more
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview text - fills remaining space */}
      <div className="flex-1 min-w-0 mt-0.5">
        <span className="text-xs font-medium text-zinc-600 truncate block">{getPreview()}</span>
      </div>

      {/* Date - right aligned */}
      <div className="shrink-0 text-xs font-semibold text-zinc-600 w-24 text-right whitespace-nowrap mt-0.5">
        {formatDate(email.timestamp)}
      </div>
    </div>
  );
};

export const EmailsView: React.FC = () => {
  const { communications, isLoading, refreshCommunications, markAsRead, matterId } =
    useClientCommunications();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState<boolean | null>(null);

  // Sync emails mutation - actually fetches new emails from Front
  const [syncEmails] = useSyncEmailsMutation();

  // Filter to only emails
  const emails = communications.filter((c) => c.type === 'email');

  // Mark all emails as read
  const handleMarkAllAsRead = () => {
    emails.forEach((email) => markAsRead(email.id));
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === emails.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(emails.map((e) => e.id)));
    }
  };

  // Enhanced refresh handler with actual email sync
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    setRefreshSuccess(null);

    try {
      // Actually sync emails from Front (this fetches new emails)
      await syncEmails({ matterId }).unwrap();
      // Also refresh the communications list to show any new data
      await refreshCommunications();
      setRefreshSuccess(true);
    } catch {
      setRefreshSuccess(false);
    } finally {
      setIsRefreshing(false);
      // Clear success/error state after animation completes
      setTimeout(() => setRefreshSuccess(null), 2000);
    }
  }, [isRefreshing, matterId, syncEmails, refreshCommunications]);

  const allSelected = emails.length > 0 && selectedIds.size === emails.length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px]">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center px-4">
        <div className="w-12 h-12 rounded-full bg-zinc-200 flex items-center justify-center mb-4">
          <Mail className="h-6 w-6 text-zinc-600" />
        </div>
        <p className="text-base font-bold text-zinc-900 mb-1">No emails</p>
        <p className="text-sm font-medium text-zinc-600">
          Email threads with this client will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 py-2">
      {/* Email table container with border and rounded corners */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        {/* Toolbar Row */}
        <div className="flex items-center gap-3 px-4 h-10 border-b border-zinc-100 bg-zinc-50/50">
          {/* Select All Checkbox */}
          <div
            className={cn(
              'relative shrink-0 size-4 rounded-[4px] border-2 cursor-pointer',
              allSelected ? 'bg-primary border-primary' : 'border-zinc-300',
            )}
            onClick={handleSelectAll}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleSelectAll();
            }}
            role="checkbox"
            aria-checked={allSelected}
            tabIndex={0}
          >
            {allSelected && (
              <Check
                className="absolute inset-0 w-3 h-3 text-white m-auto pointer-events-none"
                strokeWidth={3}
              />
            )}
          </div>

          {/* Refresh Button */}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={cn(
              'p-1.5 rounded-md transition-all duration-200',
              'hover:bg-zinc-200 active:scale-95',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              refreshSuccess === true && 'bg-emerald-50',
              refreshSuccess === false && 'bg-red-50',
            )}
            title={isRefreshing ? 'Syncing emails...' : 'Refresh emails'}
          >
            {refreshSuccess === true ? (
              <Check className="h-4 w-4 text-emerald-600 animate-in zoom-in-50 duration-200" />
            ) : refreshSuccess === false ? (
              <RefreshCw className="h-4 w-4 text-red-500" />
            ) : (
              <RefreshCw
                className={cn(
                  'h-4 w-4 text-zinc-600 transition-transform',
                  isRefreshing && 'animate-spin',
                )}
              />
            )}
          </button>

          {/* More Options Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="p-1 hover:bg-zinc-200 rounded transition-colors"
                title="More actions"
              >
                <MoreHorizontal className="h-4 w-4 text-zinc-600" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={handleMarkAllAsRead} className="cursor-pointer">
                Mark all as read
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Selection count */}
          {selectedIds.size > 0 && (
            <span className="ml-2 text-sm font-semibold text-zinc-800">
              {selectedIds.size} selected
            </span>
          )}
        </div>

        {/* Email list */}
        <div>
          {emails.map((email, index) => (
            <EmailRow
              key={email.id}
              email={email}
              isSelected={selectedIds.has(email.id)}
              onToggleSelect={handleToggleSelect}
              isLast={index === emails.length - 1}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmailsView;
